import React, { useState, useEffect, useCallback, memo } from "react";
import { Plus, Search, Edit, Trash2, Package, X, Check } from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import ApiService from "../../../services/api";

interface Product {
  clinic_product_price_id: number;
  product_id: number;
  product_name: string;
  price: number;
  clinic_id: number;
  created_at: string;
  image?: string;
}

interface ReferenceProduct {
  product_id: number;
  name: string;
  price: number;
}

interface ProductsTabProps {
  clinicId: number;
}

interface ProductFormData {
  product_id: number;
  price: string | number;
}

// Separate ProductForm component to prevent re-renders
const ProductForm = memo(
  ({
    initialData,
    onSubmit,
    onCancel,
    isSubmitting,
    isEditing,
    availableProducts,
    selectedProductName,
  }: {
    initialData: ProductFormData;
    onSubmit: (data: ProductFormData) => void;
    onCancel: () => void;
    isSubmitting: boolean;
    isEditing: boolean;
    availableProducts: ReferenceProduct[];
    selectedProductName?: string;
  }) => {
    const { user } = useAuth();
    const [formData, setFormData] = useState<ProductFormData>(initialData);

    // Reset form data when initialData changes
    useEffect(() => {
      setFormData(initialData);
    }, [initialData]);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      onSubmit(formData);
    };

    const updateProductId = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setFormData((prev) => ({
        ...prev,
        product_id: parseInt(e.target.value),
      }));
    };

    const updatePrice = (e: React.ChangeEvent<HTMLInputElement>) => {
      let val = e.target.value;

      // allow only digits
      if (!/^\d*$/.test(val)) return;

      // normalize leading zeros (except for single 0)
      if (val.length > 1) {
        val = val.replace(/^0+/, "");
        if (val === "") val = "0";
      }

      setFormData((prev) => ({
        ...prev,
        price: val === "" ? "" : +val,
      }));
    };

    const isFormValid = isEditing
      ? +formData.price > 0
      : formData.product_id !== 0 && +formData.price > 0;

    return (
      <form onSubmit={handleSubmit}>
        <div className="space-y-4 sm:space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product {!isEditing && "*"}
            </label>
            {isEditing ? (
              <input
                type="text"
                value={selectedProductName || ""}
                disabled
                className="block w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg shadow-sm bg-gray-50 text-gray-500 cursor-not-allowed text-sm sm:text-base"
              />
            ) : (
              <select
                value={formData.product_id}
                onChange={(e) => {
                  const selectedId = parseInt(e.target.value);
                  const selectedProduct = availableProducts.find(
                    (p) => p.product_id === selectedId
                  );

                  setFormData((prev) => ({
                    ...prev,
                    product_id: selectedId,
                    price: selectedProduct ? selectedProduct.price : prev.price,
                  }));
                }}
                className="block w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base"
                required
              >
                <option value={0}>Select a product</option>
                {availableProducts.map((product) => (
                  <option key={product.product_id} value={product.product_id}>
                    {product.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium text-sm sm:text-base">
                $
              </span>
              <input
                type="text"
                step="10"
                min="0"
                value={formData.price}
                onChange={updatePrice}
                className="block w-full pl-7 sm:pl-8 pr-3 sm:pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base"
                placeholder="0"
                required
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="w-full sm:w-auto px-4 sm:px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !isFormValid}
            className="w-full sm:w-auto px-4 sm:px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {isEditing ? "Updating..." : "Adding..."}
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                {isEditing ? "Update Product" : "Add Product"}
              </>
            )}
          </button>
        </div>
      </form>
    );
  }
);

const ProductsTab: React.FC<ProductsTabProps> = ({ clinicId }) => {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [referenceProducts, setReferenceProducts] = useState<
    ReferenceProduct[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const handleDeleteClick = (id: number) => {
    setSelectedId(id);
    setConfirmOpen(true);
  };

  // Form data for modals
  const [addFormData, setAddFormData] = useState<ProductFormData>({
    product_id: 0,
    price: 0,
  });
  const [editFormData, setEditFormData] = useState<ProductFormData>({
    product_id: 0,
    price: 0,
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchProducts();
  }, [clinicId, debouncedSearchTerm]);

  useEffect(() => {
    fetchReferenceProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params: any = { clinic_id: clinicId };

      if (debouncedSearchTerm.trim()) {
        params.search_text = debouncedSearchTerm.trim();
      }

      const result = await ApiService.searchClinicProducts(params);

      if (result.success && result.data) {
        setProducts(result.data);
      } else {
        setError(result.message || "Failed to fetch products");
      }
    } catch (err: any) {
      console.error("Error fetching products:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch products"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReferenceProducts = async () => {
    try {
      const result = await ApiService.getProductsList();

      if (result.success && result.data) {
        setReferenceProducts(result.data);
      } else {
        console.error("Failed to fetch reference products");
      }
    } catch (err) {
      console.error("Error fetching reference products:", err);
    }
  };

  const handleAddProduct = async (formData: ProductFormData) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const result = await ApiService.upsertClinicProducts({
        clinic_id: clinicId,
        products: [formData],
      });

      if (result.success) {
        setIsAddModalOpen(false);
        setAddFormData({ product_id: 0, price: 0 });
        fetchProducts();
      } else {
        setError(result.message || "Failed to add product");
      }
    } catch (err: any) {
      console.error("Error adding product:", err);
      setError(
        err?.response?.data?.message || err?.message || "Failed to add product"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProduct = async (formData: ProductFormData) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const result = await ApiService.upsertClinicProducts({
        clinic_id: clinicId,
        products: [formData],
      });

      if (result.success) {
        setIsEditModalOpen(false);
        setSelectedProduct(null);
        setEditFormData({ product_id: 0, price: 0 });
        fetchProducts();
      } else {
        setError(result.message || "Failed to update product");
      }
    } catch (err: any) {
      console.error("Error updating product:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update product"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProduct = async () => {
    // if (!confirm("Are you sure you want to delete this product?")) {
    //   return;
    // }
    if (!selectedId) return;

    try {
      setError(null);

      const result = await ApiService.deleteClinicProduct({
        clinic_product_price_id: selectedId,
      });

      if (result.success) {
        fetchProducts();
      } else {
        setError(result.message || "Failed to delete product");
      }
    } catch (err: any) {
      console.error("Error deleting product:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to delete product"
      );
    } finally {
      setConfirmOpen(false);
      setSelectedId(null);
    }
  };

  const openAddModal = useCallback(() => {
    setAddFormData({ product_id: 0, price: 0 });
    setIsAddModalOpen(true);
  }, []);

  const openEditModal = useCallback((product: Product) => {
    setSelectedProduct(product);
    setEditFormData({
      product_id: product.product_id,
      price: product.price,
    });
    setIsEditModalOpen(true);
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getProductName = (productId: number) => {
    const refProduct = referenceProducts.find(
      (p) => p.product_id === productId
    );
    return refProduct ? refProduct.name : `Product ${productId}`;
  };

  const getAvailableProducts = () => {
    const usedProductIds = products.map((p) => p.product_id);
    return referenceProducts.filter(
      (p) => !usedProductIds.includes(p.product_id)
    );
  };

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  // Filter products based on search
  const filteredProducts = products.filter((product) => {
    const productName = getProductName(product.product_id);
    return productName
      .toLowerCase()
      .includes(debouncedSearchTerm.toLowerCase());
  });

  // Enhanced Modal Component
  const Modal = ({
    isOpen,
    onClose,
    title,
    children,
    maxWidth = "max-w-lg",
  }: {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    maxWidth?: string;
  }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div
          className="fixed inset-0 bg-black/7 backdrop-blur-sm transition-all duration-300 ease-out"
          onClick={onClose}
        />
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <div
            className={`relative transform overflow-hidden rounded-2xl bg-white px-4 py-4 text-left shadow-2xl transition-all duration-300 ease-out sm:my-8 sm:w-full ${maxWidth} sm:p-6`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 leading-6">
                {title}
              </h3>
              <button
                onClick={onClose}
                className="rounded-full p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div>{children}</div>
          </div>
        </div>
      </div>
    );
  };

  // Mobile-friendly Product Card Component
  const ProductCard = ({ product }: { product: Product }) => (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          <div
            className={
              product?.image
                ? "h-12 w-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0"
                : "h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0"
            }
          >
            {product?.image ? (
              <img
                src={product?.image}
                alt={product.product_name}
                // className="h-10 w-10"
                className="h-full w-full object-cover"
              />
            ) : (
              <Package className="h-6 w-6 text-blue-600" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              {getProductName(product.product_id)}
            </h3>
            <p className="text-xs text-gray-500 mb-2">
              Product ID: {product.product_id}
            </p>
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-gray-900">
                ${product.price.toFixed(2)}
              </span>
              <span className="text-xs text-gray-500">
                {formatDate(product.created_at)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2 ml-3">
          <button
            onClick={() => openEditModal(product)}
            className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit product"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDeleteClick(product.clinic_product_price_id)}
            className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete product"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
            Products
          </h3>
          <p className="text-sm sm:text-base text-gray-600">
            Manage products for this account
          </p>
        </div>
        <button
          onClick={openAddModal}
          className={`${
            user?.role === "admin" ? "" : "hidden"
          } inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base`}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Search and Table Container */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
        {/* Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4 sm:mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>
          </div>
        </div>

        {/* Products Content */}
        <>
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProducts.map((product) => (
                  <tr
                    key={product.clinic_product_price_id}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div
                          className={
                            product?.image
                              ? "h-10 w-10 bg-white rounded-lg flex items-center justify-center mr-4"
                              : "h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4"
                          }
                        >
                          {product?.image ? (
                            <img
                              src={product?.image}
                              alt={product.product_name}
                              // className="h-10 w-10"
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <Package className="h-5 w-5 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {getProductName(product.product_id)}
                          </div>
                          <div className="text-sm text-gray-500">
                            Product ID: {product.product_id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        ${product.price.toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(product.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => openEditModal(product)}
                          className={`${
                            user?.role === "admin" ? "" : "hidden"
                          } text-blue-600 hover:text-blue-900 p-1`}
                          title="Edit product"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteClick(product.clinic_product_price_id)
                          }
                          className="text-red-600 hover:text-red-900 p-1"
                          title="Delete product"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile/Tablet Card View */}
          <div className="lg:hidden space-y-4">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.clinic_product_price_id}
                product={product}
              />
            ))}
          </div>

          {/* Empty State */}
          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No products found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {debouncedSearchTerm
                  ? "Try adjusting your search."
                  : "Get started by adding your first product."}
              </p>
              {!debouncedSearchTerm && (
                <button
                  onClick={openAddModal}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </button>
              )}
            </div>
          )}
        </>
      </div>

      {/* Add Product Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New Product"
      >
        <ProductForm
          initialData={addFormData}
          onSubmit={handleAddProduct}
          onCancel={() => setIsAddModalOpen(false)}
          isSubmitting={isSubmitting}
          isEditing={false}
          availableProducts={getAvailableProducts()}
        />
      </Modal>

      {/* Edit Product Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Product"
      >
        <ProductForm
          initialData={editFormData}
          onSubmit={handleUpdateProduct}
          onCancel={() => setIsEditModalOpen(false)}
          isSubmitting={isSubmitting}
          isEditing={true}
          availableProducts={[]}
          selectedProductName={
            selectedProduct ? getProductName(selectedProduct.product_id) : ""
          }
        />
      </Modal>

      {/* Confirmation Modal */}
      {confirmOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/7 backdrop-blur-sm bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-80">
            <h2 className="text-lg font-semibold mb-4">Delete Product</h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this product?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProduct}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsTab;
